import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  getExpenses, 
  createExpense, 
  updateExpense, 
  deleteExpense,
  getExpenseCategories,
  uploadReceipt,
  deleteReceipt,
  exportExpensesPdf
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { 
  Plus, 
  Trash2, 
  Edit, 
  Receipt, 
  Calendar,
  Tag,
  Search,
  Filter,
  Download,
  TrendingDown,
  Loader2,
  Upload,
  FileImage,
  X,
  Eye,
  HelpCircle,
  Info,
  Package
} from 'lucide-react';
import { toast } from 'sonner';

const EXPENSE_CATEGORIES = [
  { id: 'achat_marchandise', name: 'Achats marchandise / Stock', icon: '📦' },
  { id: 'materiel', name: 'Matériel & Équipement', icon: '🛠️' },
  { id: 'logiciels', name: 'Logiciels & Abonnements', icon: '💻' },
  { id: 'deplacement', name: 'Déplacements & Transport', icon: '🚗' },
  { id: 'expedition', name: "Frais d'expédition", icon: '📮' },
  { id: 'repas', name: 'Repas & Restauration', icon: '🍽️' },
  { id: 'bureau', name: 'Fournitures de bureau', icon: '📎' },
  { id: 'marketing', name: 'Marketing & Publicité', icon: '📢' },
  { id: 'formation', name: 'Formation & Conférences', icon: '🎓' },
  { id: 'assurance', name: 'Assurances', icon: '🛡️' },
  { id: 'loyer', name: 'Loyer & Charges', icon: '🏢' },
  { id: 'telecom', name: 'Téléphone & Internet', icon: '📱' },
  { id: 'bancaire', name: 'Frais bancaires', icon: '🏦' },
  { id: 'autre', name: 'Autre', icon: '📋' }
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.05 } }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
};

const ExpensesPage = () => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [selectedExpenseForReceipt, setSelectedExpenseForReceipt] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    category: 'autre',
    date: new Date().toISOString().split('T')[0],
    supplier: '',
    notes: '',
    currency: 'CHF',
    pro_percent: 100
  });

  useEffect(() => {
    loadExpenses();
  }, []);

  const loadExpenses = async () => {
    try {
      const res = await getExpenses();
      setExpenses(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des dépenses');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      description: '',
      amount: '',
      category: 'autre',
      date: new Date().toISOString().split('T')[0],
      supplier: '',
      notes: '',
      currency: 'CHF',
      pro_percent: 100
    });
    setEditingExpense(null);
  };

  const handleOpenDialog = (expense = null) => {
    if (expense) {
      setEditingExpense(expense);
      setFormData({
        description: expense.description,
        amount: expense.amount.toString(),
        category: expense.category,
        date: expense.date,
        supplier: expense.supplier || '',
        notes: expense.notes || '',
        currency: expense.currency || 'CHF',
        pro_percent: expense.pro_percent !== undefined ? expense.pro_percent : 100
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.description || !formData.amount) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...formData,
        amount: parseFloat(formData.amount),
        pro_percent: parseInt(formData.pro_percent) || 100
      };

      if (editingExpense) {
        await updateExpense(editingExpense.id, data);
        toast.success('Dépense mise à jour');
      } else {
        await createExpense(data);
        toast.success('Dépense ajoutée');
      }
      setDialogOpen(false);
      resetForm();
      loadExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette dépense ?')) return;
    try {
      await deleteExpense(id);
      toast.success('Dépense supprimée');
      loadExpenses();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleReceiptUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedExpenseForReceipt) return;
    
    setUploadingReceipt(true);
    try {
      await uploadReceipt(selectedExpenseForReceipt.id, file);
      toast.success('Justificatif uploadé avec succès');
      setReceiptDialogOpen(false);
      setSelectedExpenseForReceipt(null);
      loadExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploadingReceipt(false);
    }
  };

  const handleDeleteReceipt = async (expenseId) => {
    if (!window.confirm('Supprimer ce justificatif ?')) return;
    try {
      await deleteReceipt(expenseId);
      toast.success('Justificatif supprimé');
      loadExpenses();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openReceiptDialog = (expense) => {
    setSelectedExpenseForReceipt(expense);
    setReceiptDialogOpen(true);
  };

  const viewReceipt = (expenseId) => {
    const token = localStorage.getItem('token');
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/expenses/${expenseId}/receipt?auth=${token}`, '_blank');
  };

  const formatCurrency = (amount, currency = 'CHF') => {
    return new Intl.NumberFormat('fr-CH', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0) + ' ' + currency;
  };

  const getCategoryInfo = (categoryId) => {
    return EXPENSE_CATEGORIES.find(c => c.id === categoryId) || EXPENSE_CATEGORIES[EXPENSE_CATEGORIES.length - 1];
  };

  // Filter expenses
  const filteredExpenses = expenses.filter(expense => {
    const matchesSearch = expense.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (expense.supplier && expense.supplier.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = filterCategory === 'all' || expense.category === filterCategory;
    const matchesYear = filterYear === 'all' || expense.date.startsWith(filterYear);
    return matchesSearch && matchesCategory && matchesYear;
  });

  // Calculate totals
  const totalExpenses = filteredExpenses.reduce((sum, exp) => sum + exp.amount, 0);
  const totalDeductible = filteredExpenses.reduce((sum, exp) => sum + (exp.amount * (exp.pro_percent !== undefined ? exp.pro_percent : 100) / 100), 0);
  const expensesByCategory = EXPENSE_CATEGORIES.map(cat => ({
    ...cat,
    total: filteredExpenses.filter(exp => exp.category === cat.id).reduce((sum, exp) => sum + exp.amount, 0),
    deductible: filteredExpenses.filter(exp => exp.category === cat.id).reduce((sum, exp) => sum + (exp.amount * (exp.pro_percent !== undefined ? exp.pro_percent : 100) / 100), 0)
  })).filter(cat => cat.total > 0).sort((a, b) => b.total - a.total);

  // Get unique years - include current year and previous years
  const currentYear = new Date().getFullYear();
  const defaultYears = Array.from({ length: 6 }, (_, i) => String(currentYear - i));
  const dataYears = expenses.map(exp => exp.date.substring(0, 4));
  const years = [...new Set([...defaultYears, ...dataYears])].sort((a, b) => b - a);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="expenses-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold text-slate-900">Dépenses</h1>
          <p className="text-slate-500 mt-1">Gérez vos dépenses professionnelles pour votre déclaration fiscale</p>
        </div>
        <Button 
          variant="outline"
          onClick={async () => {
            try {
              const y = filterYear === 'all' ? '' : filterYear;
              const res = await exportExpensesPdf(y);
              const url = window.URL.createObjectURL(new Blob([res.data]));
              const a = document.createElement('a'); a.href = url; a.setAttribute('download', `depenses_${y || 'toutes'}.pdf`);
              document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
            } catch (err) { console.error('Failed to delete expense:', err); }
          }}
          data-testid="export-expenses-pdf"
        >
          <Download className="w-4 h-4 mr-2" />
          PDF
        </Button>
        <Button 
          onClick={() => handleOpenDialog()}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="add-expense-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle dépense
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total depenses</p>
                <p className="text-xl font-bold text-slate-900">{formatCurrency(totalExpenses)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                <Receipt className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Part deductible</p>
                <p className="text-xl font-bold text-emerald-700">{formatCurrency(totalDeductible)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <Receipt className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Nombre de depenses</p>
                <p className="text-xl font-bold text-slate-900">{filteredExpenses.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                <Tag className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Categories utilisees</p>
                <p className="text-xl font-bold text-slate-900">{expensesByCategory.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input 
                placeholder="Rechercher une dépense..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="expense-search"
              />
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-[200px]" data-testid="expense-category-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Catégorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les catégories</SelectItem>
                {EXPENSE_CATEGORIES.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.icon} {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterYear} onValueChange={setFilterYear}>
              <SelectTrigger className="w-[120px]" data-testid="expense-year-filter">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Année" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                {years.map(year => (
                  <SelectItem key={year} value={year}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Category Breakdown */}
      {expensesByCategory.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-base">Répartition par catégorie</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {expensesByCategory.slice(0, 5).map(cat => (
                <div key={cat.id} className="flex items-center gap-3">
                  <span className="text-lg">{cat.icon}</span>
                  <div className="flex-1">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-700">{cat.name}</span>
                      <div className="text-right">
                        <span className="font-medium">{formatCurrency(cat.total)}</span>
                        {cat.deductible < cat.total && <span className="text-xs text-emerald-600 ml-2">deductible: {formatCurrency(cat.deductible)}</span>}
                      </div>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-blue-500 rounded-full"
                        style={{ width: `${(cat.total / totalExpenses) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Expenses List */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="space-y-3"
      >
        {filteredExpenses.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="p-12 text-center">
              <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-1">Aucune dépense</h3>
              <p className="text-slate-500 mb-4">Commencez à enregistrer vos dépenses professionnelles</p>
              <Button onClick={() => handleOpenDialog()}>
                <Plus className="w-4 h-4 mr-2" />
                Ajouter une dépense
              </Button>
            </CardContent>
          </Card>
        ) : (
          filteredExpenses.map(expense => {
            const category = getCategoryInfo(expense.category);
            return (
              <motion.div key={expense.id} variants={itemVariants}>
                <Card className="border-slate-200 hover:border-slate-300 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-lg">
                          {category.icon}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-slate-900">{expense.description}</p>
                            {expense.linked_product_id && (
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                                <Package className="w-3 h-3" />
                                Stock
                              </span>
                            )}
                            {expense.has_receipt && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                                <FileImage className="w-3 h-3" />
                                Justificatif
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-sm text-slate-500">
                            <span>{category.name}</span>
                            {expense.supplier && (
                              <>
                                <span>•</span>
                                <span>{expense.supplier}</span>
                              </>
                            )}
                            <span>•</span>
                            <span>{new Date(expense.date).toLocaleDateString('fr-CH')}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <span className="font-semibold text-slate-900">
                            {formatCurrency(expense.amount, expense.currency)}
                          </span>
                          {expense.pro_percent !== undefined && expense.pro_percent < 100 && (
                            <div className="text-xs text-emerald-600">{expense.pro_percent}% pro = {formatCurrency(expense.amount * expense.pro_percent / 100, expense.currency)}</div>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          {expense.has_receipt ? (
                            <>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => viewReceipt(expense.id)}
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                title="Voir le justificatif"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleDeleteReceipt(expense.id)}
                                className="text-orange-500 hover:text-orange-700 hover:bg-orange-50"
                                title="Supprimer le justificatif"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </>
                          ) : (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => openReceiptDialog(expense)}
                              className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                              title="Ajouter un justificatif"
                            >
                              <Upload className="w-4 h-4" />
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleOpenDialog(expense)}
                            data-testid={`edit-expense-${expense.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDelete(expense.id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-expense-${expense.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })
        )}
      </motion.div>

      {/* Legal notice - Swiss law */}
      <div className="mt-6 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <p>
          Selon l'art. 958f CO (droit suisse), les pieces comptables et justificatifs doivent etre conserves pendant <strong>10 ans</strong> minimum. 
          Vos justificatifs uploades sont stockes de maniere securisee et indefiniment.
        </p>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingExpense ? 'Modifier la dépense' : 'Nouvelle dépense'}
            </DialogTitle>
            {!editingExpense && (
              <Link 
                to="/app/faq" 
                className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 hover:underline mt-1"
                onClick={() => setDialogOpen(false)}
              >
                <HelpCircle className="w-3.5 h-3.5" />
                Quelles dépenses puis-je déduire ?
              </Link>
            )}
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Description *</Label>
              <Input 
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Ex: Achat d'ordinateur portable"
                data-testid="expense-description-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Montant *</Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0.00"
                  data-testid="expense-amount-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Devise</Label>
                <Select 
                  value={formData.currency} 
                  onValueChange={(v) => setFormData({ ...formData, currency: v })}
                >
                  <SelectTrigger data-testid="expense-currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CHF">CHF</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Catégorie</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(v) => setFormData({ ...formData, category: v })}
                >
                  <SelectTrigger data-testid="expense-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPENSE_CATEGORIES.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.icon} {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Date</Label>
                <Input 
                  type="date"
                  min="2020-01-01"
                  max="2030-12-31"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  data-testid="expense-date-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Fournisseur</Label>
              <Input 
                value={formData.supplier}
                onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                placeholder="Ex: Apple Store"
                data-testid="expense-supplier-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea 
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Notes additionnelles..."
                rows={2}
                data-testid="expense-notes-input"
              />
            </div>

            <div className="space-y-2">
              <Label>% utilisation professionnelle</Label>
              <div className="flex items-center gap-3">
                <Input 
                  type="number"
                  min="0"
                  max="100"
                  value={formData.pro_percent}
                  onChange={(e) => setFormData({ ...formData, pro_percent: Math.min(100, Math.max(0, parseInt(e.target.value) || 0)) })}
                  className="w-24"
                  data-testid="expense-pro-percent"
                />
                <span className="text-sm text-slate-500">%</span>
                <div className="flex gap-1.5 flex-1">
                  {[25, 50, 75, 100].map(p => (
                    <button key={p} type="button" onClick={() => setFormData({ ...formData, pro_percent: p })}
                      className={`text-xs px-2 py-1 rounded-md border transition-colors ${formData.pro_percent === p ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-500 border-slate-200 hover:border-blue-300'}`}>
                      {p}%
                    </button>
                  ))}
                </div>
              </div>
              {formData.amount && formData.pro_percent < 100 && (
                <p className="text-xs text-emerald-600">
                  Montant deductible : {formatCurrency(parseFloat(formData.amount || 0) * formData.pro_percent / 100, formData.currency)} sur {formatCurrency(parseFloat(formData.amount || 0), formData.currency)}
                </p>
              )}
              <p className="text-xs text-slate-400">Part de l'achat utilisee pour l'activite professionnelle (ex: bureau a domicile, vehicule, stockage)</p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-expense-btn">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  editingExpense ? 'Mettre à jour' : 'Ajouter'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Receipt Upload Dialog */}
      <Dialog open={receiptDialogOpen} onOpenChange={setReceiptDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Ajouter un justificatif</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedExpenseForReceipt && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium">{selectedExpenseForReceipt.description}</p>
                <p className="text-sm text-slate-500">
                  {formatCurrency(selectedExpenseForReceipt.amount, selectedExpenseForReceipt.currency)} • {new Date(selectedExpenseForReceipt.date).toLocaleDateString('fr-CH')}
                </p>
              </div>
            )}
            
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
              <input 
                type="file"
                ref={fileInputRef}
                onChange={handleReceiptUpload}
                accept=".jpg,.jpeg,.png,.gif,.webp,.pdf"
                className="hidden"
              />
              <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
              <p className="text-sm text-slate-600 mb-2">
                Formats acceptés : JPG, PNG, GIF, WebP, PDF
              </p>
              <p className="text-xs text-slate-400 mb-4">
                Taille max : 10 MB
              </p>
              <Button 
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingReceipt}
              >
                {uploadingReceipt ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Upload en cours...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Choisir un fichier
                  </>
                )}
              </Button>
            </div>
            
            <p className="text-xs text-slate-500 text-center">
              💡 Conservez vos justificatifs originaux pendant 10 ans minimum
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExpensesPage;
